import { Injectable } from '@angular/core';
import { GraphQLResult, generateClient } from 'aws-amplify/api';
import { EMPTY, Observable, defer, from, of, timer, } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { awsConfig } from '../../../../../../../libs/includes/aws-export';

export type GraphQLAuthMode = 'apiKey' | 'oidc' | 'userPool' | 'iam' | 'identityPool' | 'lambda' | 'none';

type queryInput = {
  statement: string;
  variables: { [key: string]: any };
  type: string;
  iam?: boolean;
}

type subscriptionIput = {
  statement: string;
  variables: { [key: string]: any };
  type: string;
  iam?: boolean;
}

const APP_CONFIG = awsConfig.clientApp;

@Injectable({
  providedIn: 'root'
})

export class ApiService {

  private client = generateClient({
    endpoint: APP_CONFIG.API.GraphQL.endpoint,
    authMode: APP_CONFIG.API.GraphQL.defaultAuthMode as Exclude<GraphQLAuthMode, 'apiKey'>,
  });
  private subscriptionCache = new Map<string, Observable<any>>();

  graphql<V>({ statement: query, variables, type, iam = false,  }: queryInput) {

    const payload: any = {
      query,
      authMode: iam ? 'iam' : 'userPool',
    }
    if (variables) {
      payload.variables = variables;
    }
    // defer makes the Observable based on Promise retryable.
    return defer(() => {
      return from(this.client.graphql(payload) as Promise<GraphQLResult<object>>)
        .pipe(
          map(res => res.data[type as keyof object]),
          catchError((error: GraphQLResult<object> | Error) => {
            // graphql error
            if ('errors' in error) {
              const gqError = error.errors![0];
              throw new Error(`Graphql Error running query ${type}. Authmode ${iam ? 'IAM' : 'Cognito'}. Error: ${gqError?.message} ${gqError?.path ? gqError.path.toString() : ''}`);
              // normal error
            } else if ('stack' in error) {
              throw new Error(`Runtime error running query ${type}. Authmode ${iam ? 'IAM' : 'Cognito'}. Error: ${error.stack}}`);
              // fallback for something else
            } else {
              throw new Error(`Unknown Error running query ${type}. Authmode ${iam ? 'IAM' : 'Cognito'}. Error: ${error}}`);
            }
          })
        ) as Observable<V>
    })
  }

   subscription<T>({ statement: query, variables, type, iam }: subscriptionIput): Observable<T> {
    return this.ensureConnectivityOnce(2000).pipe(   // 2s retry delay
      switchMap(online => {
        if (!online) {
          console.warn(`API call ${type} cancelled – still offline after retry`);
          return EMPTY as Observable<T>;
        }
        return this.executeSubscription({ statement: query, variables, type, iam }) as Observable<T>
      })
    );
  }

   private ensureConnectivityOnce(
    retryDelayMs = 2000
  ): Observable<boolean> {
    if (navigator.onLine) {
      return of(true);
    }

    console.warn(`Device offline – retrying in ${retryDelayMs}ms`);

    return timer(retryDelayMs).pipe(
      map(() => {
        const online = navigator.onLine;
        if (!online) {
          console.warn('Still offline after retry – cancelling operation');
        }
        return online;
      })
    );
  }


  private isUnauthorizedGraphQLError(error: any): boolean {
    try {
      if (error && 'errors' in error && Array.isArray(error.errors)) {
        const first = error.errors[0];
        const msg = String(first?.message ?? '').toLowerCase();
        const type = String(first?.errorType ?? '').toLowerCase();

        if (
          msg.includes('unauthorized') ||
          msg.includes('not authorized') ||
          type.includes('unauthorized') ||
          type.includes('notauthorized')
        ) {
          return true;
        }
      }

      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('unauthorized') || msg.includes('not authorized')) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  }



  private executeSubscription<T>({ statement: query, variables, type, iam }: subscriptionIput): Observable<T> {
    const payload: any = {
      query,
      authMode: iam ? 'iam' : 'userPool',
      ...(variables && { variables }),
    }

    const key = `${type}:${JSON.stringify(variables || {})}`;

    const cached = this.subscriptionCache.get(key);
    if (cached) {
      return cached as Observable<T>;
    }

    const call$ = () =>
      from(this.client.graphql(payload)).pipe(
        map(res => res?.data?.[type] as T | undefined),
        filter((v): v is T => v !== undefined),
      );

    const stream$ = call$().pipe(
      catchError((error: GraphQLResult<object> | Error) => {
        // Same semantics as executeGraphQL: retry once on Unauthorized for Cognito
        if (!iam && this.isUnauthorizedGraphQLError(error)) {
          console.warn(
            `GraphQL subscription [${type}] Unauthorized – retrying once...`,
          );

          return timer(500).pipe(
            switchMap(() => call$()),
            catchError((retryError: GraphQLResult<object> | Error) => {
              // Retry failed – now it's a "real" error
              this.logError(retryError, type, iam ?? false);
              return EMPTY as Observable<T>;
            }),
          );
        }

        // Non-Unauthorized or IAM: behave as before
        this.logError(error, type, iam ?? false);
        return EMPTY as Observable<T>;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.subscriptionCache.delete(key)),
    );

    this.subscriptionCache.set(key, stream$);
    return stream$ as Observable<T>;
  }

   private logError(error: GraphQLResult<object> | Error, type: string, iam: boolean): void {
    const authMode = iam ? 'IAM' : 'Cognito';

    if ('errors' in error) {
      const gqError = error.errors![0];
      console.error(
        `GraphQL Error [${type}] [${authMode}]:`,
        gqError?.message,
        gqError?.path ? `Path: ${gqError.path}` : ''
      );
    } else if (error instanceof Error) {
      console.error(`Runtime Error [${type}] [${authMode}]:`, error.message);
    } else {
      console.error(`Unknown Error [${type}] [${authMode}]:`, error);
    }
  }

}
